import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import withLayout from "../layout";
import RentHeader, { RentFooter } from "../rent/RentHeader";
import { createCheckout, findSession, getLocation, rememberBooking } from "../rent/api";
import { euro, fmtDateTime, isNightType } from "../rent/tariffs";
import { Icons, tariffName } from "../rent/ui";
import { buildPlate } from "../booking";
import "../login/login.css";

/*
 * Digital bezahlen vor Ort – Teil des Rent-Portals.
 * QR-Code am Schild: go.innopark24.de/<Parkplatz>  (auch rent.innopark24.de/pay/<Parkplatz>)
 * Kunde gibt nur sein Kennzeichen ein, das Backend findet den offenen Parkvorgang an diesem Parkplatz.
 */

// Kurzadressen: go.innopark24.de/<Parkplatz> (Hauptadresse) bzw. pay.<domain>/<Parkplatz>
export const isShortHost = () => typeof window !== "undefined" && /^(go|pay)\./i.test(window.location.hostname);
export const isPayHost = () => typeof window !== "undefined" && /^pay\./i.test(window.location.hostname);
// Frist für die Online-Zahlung nach der Ausfahrt (Stunden) – per REACT_APP_PAY_HOURS änderbar
export const PAY_HOURS = Number(process.env.REACT_APP_PAY_HOURS) || 48;

const payPath = (code) => (isShortHost() ? `/${encodeURIComponent(code)}` : `/pay/${encodeURIComponent(code)}`);

const PlateField = ({ id, country, setCountry, plate, setPlate, error, t }) => (
  <div className={`ipl-field ${error ? "has-error" : ""}`}>
    <label htmlFor={id}>{t("licensePlate")}</label>
    <div className="ipl-plate-row">
      <div className="ipl-country">
        <select aria-label={t("rpCountry")} value={country} onChange={(e) => setCountry(e.target.value)}>
          <option value="de">DE</option>
          <option value="int">Int</option>
        </select>
      </div>
      <div className="ipl-input">
        {Icons.car}
        <input
          id={id}
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck="false"
          maxLength={14}
          placeholder={country === "de" ? "M-AB1234" : "AB123CD"}
          value={plate}
          onChange={(e) => setPlate(e.target.value.toUpperCase().replace(/[^A-ZÄÖÜ0-9\s-]/g, ""))}
        />
      </div>
    </div>
    <div className="ipl-err">{t(country === "de" ? "rpPlateErrorDe" : "rpPlateErrorInt")}</div>
  </div>
);

// Frist-Hinweis direkt auf der Bezahlkarte
const Deadline = ({ t }) => (
  <div className="rx-deadline">
    <b>{t("pyDeadlineBig", { hours: PAY_HOURS })}</b>
    <span>{t("pyDeadline", { hours: PAY_HOURS })}</span>
  </div>
);

// „So einfach parken Sie“ – Ablauf und Zahlungsmöglichkeiten
const HowTo = ({ t }) => {
  const steps = [
    ["gate", "pyHow1"],
    ["car", "pyHow2"],
    ["back", "pyHow3"],
    ["card", "pyHow4"],
  ];
  return (
    <section className="rx-how" aria-labelledby="py-how">
      <h2 id="py-how">{t("pyHowHead")}</h2>
      <ol className="rx-how-steps">
        {steps.map(([icon, k], i) => (
          <li key={k}>
            <span className={`rx-ic ${i === 3 ? "night" : ""}`}>{Icons[icon]}</span>
            <span>
              <b>{`${i + 1}. ${t(`${k}Head`)}`}</b>
              <span>{t(`${k}Text`, { hours: PAY_HOURS })}</span>
            </span>
          </li>
        ))}
      </ol>
      <h3>{t("pyOptHead")}</h3>
      <div className="rx-how-opts">
        <div>
          {Icons.code}
          <span>
            <b>{t("pyOpt1Head")}</b>
            <span>{t("pyOpt1Text", { hours: PAY_HOURS })}</span>
          </span>
        </div>
        <div>
          {Icons.card}
          <span>
            <b>{t("pyOpt2Head")}</b>
            <span>{t("pyOpt2Text")}</span>
          </span>
        </div>
      </div>
    </section>
  );
};

// Einstieg ohne Parkplatz im Link: nur Kennzeichen – das Backend findet den offenen Parkvorgang
const PayStartPage = ({ setLoaderBox }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialPlate = new URLSearchParams(window.location.search).get("plate") || "";
  const [country, setCountry] = useState("de");
  const [plate, setPlate] = useState(initialPlate);
  const [plateError, setPlateError] = useState(false);
  const [notFound, setNotFound] = useState(null);
  const [lookupError, setLookupError] = useState("");

  const lookup = async (raw, ctry) => {
    const p = buildPlate(raw, ctry);
    setPlateError(!p);
    if (!p) return;
    setLookupError("");
    setNotFound(null);
    setLoaderBox(true);
    try {
      const session = await findSession(null, p);
      setLoaderBox(false);
      if (session && session.parkingId) {
        navigate(`${payPath(session.parkingId)}?plate=${encodeURIComponent(p)}`);
        return;
      }
      setNotFound(p);
    } catch (err) {
      setLoaderBox(false);
      setLookupError(t("pyLookupError"));
    }
  };

  useEffect(() => {
    document.title = "Parken bezahlen | innopark24";
    if (initialPlate) lookup(initialPlate, buildPlate(initialPlate, "de") ? "de" : "int");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        <section className="rx-card rx-pay-card">
          <span className="rx-eyebrow">{t("pyEyebrow")}</span>
          <h1>{t("pyStartHead")}</h1>
          <p className="rx-muted">{t("pyStartLead")}</p>
          <Deadline t={t} />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              lookup(plate, country);
            }}
            noValidate
          >
            <PlateField id="py-plate" country={country} setCountry={setCountry} plate={plate} setPlate={(v) => { setPlate(v); setPlateError(false); setNotFound(null); }} error={plateError} t={t} />
            {lookupError && (
              <div className="ipl-status" role="alert">
                {lookupError}
              </div>
            )}
            <button className="ipl-btn" type="submit">
              <span>{t("pyFind")}</span>
            </button>
          </form>

          {notFound && (
            <div className="rx-pay-nf">
              <div className="rx-info-box rx-pay-found">
                {Icons.info}
                <div>
                  <b>{t("pyNoneHead")}</b>
                  <p className="rx-muted">{t("pyNoneText", { plate: notFound })}</p>
                </div>
              </div>
            </div>
          )}

          <p className="rx-pay-privacy">
            {Icons.lock}
            {t("pyPrivacyAll")}
          </p>
        </section>
        <HowTo t={t} />
      </main>
      <RentFooter />
    </div>
  );
};

// Bezahlseite eines Parkplatzes: /pay/:id (bzw. pay.innopark24.de/:id)
const PayLocationPage = ({ setLoaderBox }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id } = useParams();
  const initialPlate = new URLSearchParams(window.location.search).get("plate") || "";
  const [loc, setLoc] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [country, setCountry] = useState("de");
  const [plate, setPlate] = useState(initialPlate);
  const [plateError, setPlateError] = useState(false);
  const [result, setResult] = useState(null); // { plate, session | null }
  const [lookupError, setLookupError] = useState("");
  const [option, setOption] = useState(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [payError, setPayError] = useState("");

  const lookup = async (rawPlate, ctry) => {
    const p = buildPlate(rawPlate, ctry);
    setPlateError(!p);
    if (!p) return;
    setLookupError("");
    setLoaderBox(true);
    try {
      const session = await findSession(id, p);
      setResult({ plate: p, session });
      setOption(session && session.options.length ? session.options[0] : null);
    } catch (err) {
      setLookupError(t("pyLookupError"));
    }
    setLoaderBox(false);
  };

  useEffect(() => {
    document.title = "Parken bezahlen | innopark24";
    let alive = true;
    setLoaderBox(true);
    getLocation(id)
      .then((l) => {
        if (!alive) return;
        setLoc(l);
        setLoaderBox(false);
        if (initialPlate) lookup(initialPlate, buildPlate(initialPlate, "de") ? "de" : "int");
      })
      .catch(() => {
        if (!alive) return;
        setLoadError(true);
        setLoaderBox(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pay = async (e) => {
    e.preventDefault();
    setPayError("");
    const mail = email.trim();
    const mailOk = !mail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail);
    setEmailError(!mailOk);
    if (!mailOk || !agreed || !option) return;
    const tariff = loc.tariffs.find((x) => x.id === option.tariffId);
    const label = option.label || (tariff ? tariffName(tariff, t) : t(`rxT_${option.type}`));
    rememberBooking({ title: loc.title, code: loc.code || loc.id, tariff: label, type: option.type, start: option.start || result.session.entryTime, end: option.end, total: option.amount, monthly: false, fee: true, entry: result.session.entryTime, plate: result.plate, email: mail });
    setLoaderBox(true);
    try {
      const url = await createCheckout({
        parkingId: loc.id,
        sessionId: result.session.sessionId,
        tariffId: option.tariffId,
        tariffType: option.type,
        recurring: false,
        start: option.start,
        end: option.end,
        expectedTotal: option.amount,
        licensePlate: result.plate,
        email: mail || undefined,
        lang,
        successUrl: `${window.location.origin}/erfolg?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: window.location.href,
      });
      window.location.href = url;
      return;
    } catch (err) {
      setPayError(t("rpPayError"));
    }
    setLoaderBox(false);
  };

  if (loadError) {
    return (
      <div className="rp">
        <RentHeader />
        <main className="rp-main">
          <div className="rx-card rx-done" style={{ borderTopColor: "var(--ipl-blue)" }}>
            <h1>{t("rxNotFoundHead")}</h1>
            <p className="rx-muted">{t("rxNotFoundText", { code: id })}</p>
            <div className="rx-actions" style={{ marginTop: 18 }}>
              <Link className="ipl-btn" to={isPayHost() ? "/" : "/pay"}>
                <span>{t("rxTryAgain")}</span>
              </Link>
            </div>
          </div>
        </main>
        <RentFooter />
      </div>
    );
  }

  const session = result && result.session;

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        {loc && (
          <section className="rx-card rx-pay-card">
            <span className="rx-eyebrow">{t("rxSiteEyebrow", { code: loc.code || loc.id })}</span>
            <h1>{loc.title}</h1>
            <p className="rx-muted rx-pay-addr">
              {Icons.pin}
              {loc.address}
            </p>

            {!result && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  lookup(plate, country);
                }}
                noValidate
              >
                <p className="rx-pay-lead">{t("pyLead")}</p>
                <Deadline t={t} />
                <PlateField id="py-plate" country={country} setCountry={setCountry} plate={plate} setPlate={(v) => { setPlate(v); setPlateError(false); }} error={plateError} t={t} />
                {lookupError && (
                  <div className="ipl-status" role="alert">
                    {lookupError}
                  </div>
                )}
                <button className="ipl-btn" type="submit">
                  <span>{t("pyFind")}</span>
                </button>
                <p className="rx-pay-privacy">
                  {Icons.lock}
                  {t("pyPrivacy")}
                </p>
              </form>
            )}

            {result && session && (
              <form onSubmit={pay} noValidate>
                <div className="rx-info-box rx-ok-box rx-pay-found">
                  {Icons.check}
                  <div>
                    <b>{t("pyFoundHead", { plate: result.plate })}</b>
                    {session.entryTime && <p>{t("pyFoundText", { time: fmtDateTime(session.entryTime, lang) })}</p>}
                  </div>
                </div>

                {session.options.length === 0 ? (
                  <div className="rx-empty-box">{t("pyNoOptions")}</div>
                ) : (
                  <>
                    <div className="rx-pay-h">{t("pyChoose")}</div>
                    <div className="rx-options" role="radiogroup">
                      {session.options.map((o) => {
                        const tariff = loc.tariffs.find((x) => x.id === o.tariffId);
                        const label = o.label || (tariff ? tariffName(tariff, t) : t(`rxT_${o.type}`));
                        const on = option && option.tariffId === o.tariffId;
                        return (
                          <label key={o.tariffId} className={`rx-option ${on ? "on" : ""}`}>
                            <input type="radio" name="py-option" checked={on} onChange={() => setOption(o)} />
                            <span className={`rx-ic ${isNightType({ type: o.type }) ? "night" : ""}`}>{Icons[o.type] || Icons.hour}</span>
                            <span className="rx-option-txt">
                              <b>{label}</b>
                              {(o.start || o.end) && <span>{`${o.start ? fmtDateTime(o.start, lang) : ""}${o.end ? ` – ${fmtDateTime(o.end, lang)}` : ""}`}</span>}
                            </span>
                            <strong>{euro(o.amount, lang)}</strong>
                          </label>
                        );
                      })}
                    </div>

                    <div className={`ipl-field ${emailError ? "has-error" : ""}`}>
                      <label htmlFor="py-mail">{t("pyEmailOpt")}</label>
                      <div className="ipl-input">
                        {Icons.mail}
                        <input id="py-mail" type="email" autoComplete="email" placeholder="name@beispiel.de" value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(false); }} />
                      </div>
                      <div className="ipl-err">{t("rpEmailError")}</div>
                    </div>
                    <label className="ipl-check">
                      <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                      <span dangerouslySetInnerHTML={{ __html: t("rpAgreement") }}></span>
                    </label>
                    {payError && (
                      <div className="ipl-status" role="alert">
                        {payError}
                      </div>
                    )}
                    <button className="ipl-btn" type="submit" disabled={!agreed || !option}>
                      <span>{option ? t("rxPayAmount", { amount: euro(option.amount, lang) }) : t("rxStepPay")}</span>
                    </button>
                    <p className="rx-secure">
                      {Icons.lock}
                      {t("rxSecureStripe")}
                    </p>
                  </>
                )}
                <button type="button" className="rx-back rx-pay-change" onClick={() => setResult(null)}>
                  {Icons.back}
                  {t("pyChangePlate")}
                </button>
              </form>
            )}

            {result && !session && (
              <div>
                <div className="rx-info-box rx-pay-found">
                  {Icons.info}
                  <div>
                    <b>{t("pyNotFoundHead")}</b>
                    <p className="rx-muted">{t("pyNotFoundNoAdvance", { plate: result.plate })}</p>
                  </div>
                </div>
                <button type="button" className="rx-back rx-pay-change" onClick={() => setResult(null)}>
                  {Icons.back}
                  {t("pyChangePlate")}
                </button>
              </div>
            )}
          </section>
        )}
        {loc && <HowTo t={t} />}
      </main>
      <RentFooter />
    </div>
  );
};

export const PayStart = withLayout(PayStartPage);
export const PayLocation = withLayout(PayLocationPage);
